import { useEffect, useState, useCallback, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { DetailRecordCard } from "../components/DetailRecordCard";
import {
    driverAdmin,
    passengerAdmin,
    getAdminConfigWarning,
    asAdminList,
    reloadOpenDetailIf,
} from "../components/services/adminBackendFetch";
import { useAppContext } from "../providers/ProviderAppContainer";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";

const DRIVER_LIMIT_OPTIONS = [
    { label: "100", value: 100 },
    { label: "200", value: 200 },
    { label: "300", value: 300 },
    { label: "500", value: 500 },
];

const EARNINGS_RANGE_PRESETS = [
    { label: "Last 7 days", value: "7d" },
    { label: "Last 30 days", value: "30d" },
    { label: "This month", value: "month" },
    { label: "Custom range", value: "custom" },
];

const DEFAULT_COMMISSION_PERCENT = 75;

const VEHICLE_FIELD_LABELS = {
    vehicle_number: "Registration number",
    vehicle_model: "Model",
    vehicle_colour: "Colour",
    vehicle_category: "Vehicle category",
    created_on: "Created",
    updated_at: "Last updated",
};

const BANK_FIELD_LABELS = {
    account_holder_name: "Account holder",
    bank_name: "Bank name",
    account_number: "Account number",
    transit_number: "Transit / routing",
    institution_number: "Institution number",
    created_on: "Created",
    updated_at: "Last updated",
};

const VEHICLE_FIELD_ORDER = [
    "vehicle_number",
    "vehicle_model",
    "vehicle_colour",
    "vehicle_category",
    "created_on",
    "updated_at",
];
const BANK_FIELD_ORDER = [
    "account_holder_name",
    "bank_name",
    "account_number",
    "transit_number",
    "institution_number",
    "created_on",
    "updated_at",
];

/** Must match `DOCUMENT_TYPES` in uc_drivers_api (storage keys for private media). */
const DOCUMENT_TYPE_OPTIONS = [
    { type: "face", label: "Face photo" },
    { type: "driving_license_front", label: "Driving licence (front)" },
    { type: "driving_license_back", label: "Driving licence (back)" },
    { type: "vehicle_registration_front", label: "Vehicle registration (front)" },
    { type: "vehicle_registration_back", label: "Vehicle registration (back)" },
    { type: "vehicle_insurance", label: "Vehicle insurance" },
    { type: "bank_void_cheque", label: "Bank void cheque" },
];
const DRIVER_DOCUMENT_TYPE_SET = new Set(DOCUMENT_TYPE_OPTIONS.map((o) => o.type));

function boolTag(val) {
    return <Tag value={val ? "Yes" : "No"} severity={val ? "success" : "secondary"} />;
}

function driverStatusText(raw) {
    const s = String(raw ?? "").trim();
    return s || "—";
}

function commissionRateToPercent(rate) {
    const n = Number(rate);
    if (!Number.isFinite(n)) return DEFAULT_COMMISSION_PERCENT;
    return Math.round(n * 10000) / 100;
}

function commissionPercentToRate(percent) {
    const n = Number(percent);
    if (!Number.isFinite(n)) return null;
    return Math.round((n / 100) * 10000) / 10000;
}

function formatMoney(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `$${v.toFixed(2)}`;
}

function toDateOnlyString(d) {
    if (!d) return null;
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function startOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function endOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
}

function resolveEarningsDateRange(preset, customRange) {
    const today = startOfLocalDay(new Date());
    if (preset === "7d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        return { from, to: endOfLocalDay(today) };
    }
    if (preset === "30d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return { from, to: endOfLocalDay(today) };
    }
    if (preset === "month") {
        const from = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from, to: endOfLocalDay(today) };
    }
    if (Array.isArray(customRange) && customRange[0] && customRange[1]) {
        return { from: startOfLocalDay(customRange[0]), to: endOfLocalDay(customRange[1]) };
    }
    if (Array.isArray(customRange) && customRange[0]) {
        return { from: startOfLocalDay(customRange[0]), to: endOfLocalDay(customRange[0]) };
    }
    return { from: null, to: null };
}

function isOnlineStatus(raw) {
    return String(raw ?? "")
        .trim()
        .toLowerCase() === "online";
}

function resolveVehicleCategoryId(rowOrDetail) {
    if (!rowOrDetail || typeof rowOrDetail !== "object") return null;
    if (rowOrDetail.vehicle_category_id != null && String(rowOrDetail.vehicle_category_id).trim() !== "") {
        const n = Number(rowOrDetail.vehicle_category_id);
        return Number.isFinite(n) && n >= 1 ? n : null;
    }
    const v = rowOrDetail.vehicle;
    if (v && v.vehicle_category_id != null && String(v.vehicle_category_id).trim() !== "") {
        const n = Number(v.vehicle_category_id);
        return Number.isFinite(n) && n >= 1 ? n : null;
    }
    return null;
}

function humanizeDocKey(key) {
    return String(key || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function enrichVehicleForAdminDetail(vehicle, passengerVehicleTypes) {
    if (!vehicle || typeof vehicle !== "object") return null;
    const id = vehicle.vehicle_category_id;
    let vehicle_category = "—";
    if (id != null && String(id).trim() !== "") {
        const row = (passengerVehicleTypes || []).find((t) => Number(t.id) === Number(id));
        vehicle_category = row ? `${row.name} (${row.type})` : `Category id ${id}`;
    }
    return { ...vehicle, vehicle_category };
}

function documentDisplayLabel(docKey) {
    const row = DOCUMENT_TYPE_OPTIONS.find((o) => o.type === docKey);
    return row ? row.label : humanizeDocKey(docKey);
}

/** Non-empty document entries, stable order (matches driver app document types first). */
function orderedDriverDocumentEntries(detail) {
    const docs = detail?.documents;
    if (!docs || typeof docs !== "object") return [];
    const raw = Object.entries(docs).filter(([, url]) => url);
    const order = DOCUMENT_TYPE_OPTIONS.map((o) => o.type);
    const rank = (k) => {
        const i = order.indexOf(k);
        return i === -1 ? 1000 : i;
    };
    return [...raw].sort((a, b) => {
        const d = rank(a[0]) - rank(b[0]);
        if (d !== 0) return d;
        return String(a[0]).localeCompare(String(b[0]));
    });
}

function driverDialogHeader(detail) {
    if (!detail) return "Driver profile";
    const name = (detail.full_name || "").trim();
    const phone = detail.phone != null ? String(detail.phone).trim() : "";
    const email = detail.email != null ? String(detail.email).trim() : "";
    const title = name || "Driver profile";

    return (
        <div className="flex align-items-start gap-3 pr-4 min-w-0">
            <div className="uc-admin-dialog-header-icon flex align-items-center justify-content-center border-round-lg bg-primary flex-shrink-0">
                <i className="pi pi-id-card text-primary-contrast text-xl" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
                <div className="uc-admin-dialog-title">{title}</div>
                {phone || email ? (
                    <div className="uc-admin-dialog-contact flex flex-column gap-1 mt-2">
                        {phone ? (
                            <a href={`tel:${phone.replace(/\s/g, "")}`} className="uc-admin-dialog-contact-line">
                                <i className="pi pi-phone" aria-hidden />
                                <span>{phone}</span>
                            </a>
                        ) : null}
                        {email ? (
                            <a href={`mailto:${email}`} className="uc-admin-dialog-contact-line">
                                <i className="pi pi-envelope" aria-hidden />
                                <span className="line-break-anywhere">{email}</span>
                            </a>
                        ) : null}
                    </div>
                ) : (
                    <p className="uc-admin-dialog-muted mt-2 mb-0">No phone or email on file.</p>
                )}
            </div>
        </div>
    );
}

export default function DriversPage() {
    const { showToast } = useAppContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [driverLimit, setDriverLimit] = useState(300);
    const [detail, setDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editDriverId, setEditDriverId] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editDocumentKeys, setEditDocumentKeys] = useState({});
    const [pendingDocRemovals, setPendingDocRemovals] = useState([]);
    const [passengerVehicleTypes, setPassengerVehicleTypes] = useState([]);
    const [editForm, setEditForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        active: true,
        commission_percent: DEFAULT_COMMISSION_PERCENT,
        vehicle_number: "",
        vehicle_model: "",
        vehicle_colour: "",
        vehicle_category_id: null,
        bank_account_holder_name: "",
        bank_name: "",
        account_number: "",
        transit_number: "",
        institution_number: "",
    });

    const [approveOpen, setApproveOpen] = useState(false);
    const [approveDriver, setApproveDriver] = useState(null);
    const [approveSaving, setApproveSaving] = useState(false);
    const [approveForm, setApproveForm] = useState({
        vehicle_category_id: null,
        commission_percent: DEFAULT_COMMISSION_PERCENT,
        capacity: null,
    });

    const [earningsPreset, setEarningsPreset] = useState("7d");
    const [earningsCustomRange, setEarningsCustomRange] = useState(null);
    const [earningsLoading, setEarningsLoading] = useState(false);
    const [earningsData, setEarningsData] = useState(null);
    const [selectedEarnings, setSelectedEarnings] = useState([]);
    const [payoutSaving, setPayoutSaving] = useState(false);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [receiptPayload, setReceiptPayload] = useState(null);
    const [statusSavingId, setStatusSavingId] = useState(null);

    const reload = useCallback(() => {
        const warn = getAdminConfigWarning();
        if (warn) {
            showToast({ severity: "warn", summary: "Configuration", detail: warn });
            setLoading(false);
            return Promise.resolve();
        }
        setLoading(true);
        return driverAdmin(`/admin/drivers?limit=${driverLimit}`)
            .then((data) => {
                const list = asAdminList(data);
                if (!Array.isArray(data)) {
                    showToast({
                        severity: "warn",
                        summary: "Unexpected response",
                        detail: "The driver list was not in the expected format.",
                    });
                }
                setRows(list);
            })
            .catch((e) => {
                showToast({
                    severity: "error",
                    summary: "Could not load drivers",
                    detail: String(e.message || e),
                });
                setRows([]);
            })
            .finally(() => setLoading(false));
    }, [driverLimit, showToast]);

    const vehicleCategoryDropdownOptions = useMemo(() => {
        const opts = (passengerVehicleTypes || []).map((t) => ({
            label: `${t.name} (${t.type})`,
            value: Number(t.id),
        }));
        return [{ label: "Not set", value: null }, ...opts];
    }, [passengerVehicleTypes]);

    const documentEntriesOrdered = useMemo(() => orderedDriverDocumentEntries(detail), [detail]);

    useEffect(() => {
        if (getAdminConfigWarning()) {
            setPassengerVehicleTypes([]);
            return;
        }
        passengerAdmin("/admin/vehicles")
            .then((data) => {
                setPassengerVehicleTypes(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                setPassengerVehicleTypes([]);
            });
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    async function openDetail(id) {
        setDetailOpen(true);
        setDetail(null);
        setDetailLoading(true);
        setEarningsData(null);
        setSelectedEarnings([]);
        setEarningsPreset("7d");
        setEarningsCustomRange(null);
        try {
            const d = await driverAdmin(`/admin/drivers/${id}`);
            setDetail(d);
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Driver profile",
                detail: String(e.message || e),
            });
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    }

    const loadDriverEarnings = useCallback(
        async (driverId, preset, customRange) => {
            if (driverId == null) return;
            const { from, to } = resolveEarningsDateRange(preset, customRange);
            if (preset === "custom" && (!from || !to)) {
                setEarningsData(null);
                setSelectedEarnings([]);
                return;
            }
            setEarningsLoading(true);
            try {
                const qs = new URLSearchParams();
                if (from) qs.set("from", toDateOnlyString(from));
                if (to) qs.set("to", toDateOnlyString(to));
                const q = qs.toString();
                const data = await driverAdmin(
                    `/admin/drivers/${driverId}/wallet-transactions${q ? `?${q}` : ""}`,
                );
                setEarningsData(data);
                setSelectedEarnings([]);
            } catch (e) {
                showToast({
                    severity: "error",
                    summary: "Earnings history",
                    detail: String(e.message || e),
                });
                setEarningsData(null);
            } finally {
                setEarningsLoading(false);
            }
        },
        [showToast],
    );

    useEffect(() => {
        if (!detailOpen || detail?.id == null) return;
        if (earningsPreset === "custom" && (!earningsCustomRange?.[0] || !earningsCustomRange?.[1])) {
            return;
        }
        loadDriverEarnings(detail.id, earningsPreset, earningsCustomRange);
    }, [detailOpen, detail?.id, earningsPreset, earningsCustomRange, loadDriverEarnings]);

    const selectedEarningsTotal = useMemo(() => {
        return selectedEarnings.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    }, [selectedEarnings]);

    async function setDriverOnlineOffline(rowOrDetail, nextStatus) {
        const id = rowOrDetail?.id;
        if (id == null) return;
        const label = (rowOrDetail.full_name || "").trim() || `Driver #${id}`;
        setStatusSavingId(id);
        try {
            const updated = await driverAdmin(`/admin/drivers/${id}/status`, {
                method: "PATCH",
                body: JSON.stringify({ status: nextStatus }),
            });
            showToast({
                severity: "success",
                summary: nextStatus === "online" ? "Set online" : "Set offline",
                detail: label,
            });
            await reload();
            if (detailOpen && detail?.id === id) {
                setDetail(updated);
            }
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Status update failed",
                detail: String(e.message || e),
            });
        } finally {
            setStatusSavingId(null);
        }
    }

    function requestDeleteDocumentFromDetail(docType) {
        const id = detail?.id;
        if (id == null || !docType) return;
        const t = String(docType).trim();
        if (!DRIVER_DOCUMENT_TYPE_SET.has(t)) {
            showToast({
                severity: "error",
                summary: "Invalid document",
                detail: "This key is not a known driver document type.",
            });
            return;
        }
        const label = documentDisplayLabel(t);
        confirmDialog({
            message: `Remove "${label}" for this driver? This deletes the stored document row on the server.`,
            header: "Delete document",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await driverAdmin(`/admin/drivers/${id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ remove_document_types: [t] }),
                    });
                    showToast({ severity: "success", summary: "Document removed", detail: label });
                    await reload();
                    const d2 = await driverAdmin(`/admin/drivers/${id}`);
                    setDetail(d2);
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Delete failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    function requestApprove(row) {
        const id = row?.id ?? row;
        const source =
            typeof row === "object" && row != null && row.id != null
                ? row
                : detailOpen && detail?.id === id
                  ? detail
                  : null;
        const catId = resolveVehicleCategoryId(source);
        const existingRate =
            source?.commission_rate != null
                ? commissionRateToPercent(source.commission_rate)
                : detail?.commission_rate != null
                  ? commissionRateToPercent(detail.commission_rate)
                  : DEFAULT_COMMISSION_PERCENT;
        setApproveDriver(source && source.id != null ? source : { id, full_name: detail?.full_name });
        const selectedType = (passengerVehicleTypes || []).find((t) => Number(t.id) === Number(catId));
        setApproveForm({
            vehicle_category_id: catId,
            commission_percent: existingRate,
            capacity: selectedType?.capacity != null ? Number(selectedType.capacity) : null,
        });
        setApproveOpen(true);
    }

    async function submitApproveOnboarding() {
        const id = approveDriver?.id;
        if (id == null) return;
        const catId = approveForm.vehicle_category_id;
        if (catId == null || !Number.isFinite(Number(catId)) || Number(catId) < 1) {
            showToast({
                severity: "warn",
                summary: "Vehicle category required",
                detail: "Select a vehicle category before approving.",
            });
            return;
        }
        const rate = commissionPercentToRate(approveForm.commission_percent);
        if (rate == null || rate < 0.01 || rate > 1) {
            showToast({
                severity: "warn",
                summary: "Commission required",
                detail: "Enter a commission percentage between 1 and 100.",
            });
            return;
        }
        setApproveSaving(true);
        try {
            const capacity = Number(approveForm.capacity);
            if (Number.isFinite(capacity) && capacity >= 1) {
                await passengerAdmin(`/admin/vehicles/${Number(catId)}`, {
                    method: "PATCH",
                    body: JSON.stringify({ capacity: Math.floor(capacity) }),
                });
                setPassengerVehicleTypes((prev) =>
                    (prev || []).map((t) =>
                        Number(t.id) === Number(catId) ? { ...t, capacity: Math.floor(capacity) } : t,
                    ),
                );
            }
            await driverAdmin(`/admin/drivers/${id}/approve-onboarding`, {
                method: "PATCH",
                body: JSON.stringify({
                    commission_rate: rate,
                    vehicle_category_id: Number(catId),
                }),
            });
            const label = (approveDriver?.full_name || "").trim();
            showToast({ severity: "success", summary: "Approved", detail: label || "Driver updated" });
            setApproveOpen(false);
            await reload();
            await reloadOpenDetailIf({
                dialogOpen: detailOpen,
                shownId: detail?.id,
                mutatedId: id,
                fetchDetail: (i) => driverAdmin(`/admin/drivers/${i}`),
                setDetail,
            });
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Approve failed",
                detail: String(e.message || e),
            });
        } finally {
            setApproveSaving(false);
        }
    }

    async function submitPayoutSettlement() {
        const id = detail?.id;
        if (id == null || selectedEarnings.length === 0) return;
        const { from, to } = resolveEarningsDateRange(earningsPreset, earningsCustomRange);
        const total = selectedEarningsTotal;
        confirmDialog({
            message: `Mark payment done for ${selectedEarnings.length} transaction(s) totaling ${formatMoney(total)}? This clears those earnings from unpaid balance.`,
            header: "Clear driver payment",
            icon: "pi pi-wallet",
            acceptLabel: "Mark payment done",
            rejectLabel: "Cancel",
            accept: async () => {
                setPayoutSaving(true);
                try {
                    const result = await driverAdmin(`/admin/drivers/${id}/payout-settlements`, {
                        method: "POST",
                        body: JSON.stringify({
                            transaction_ids: selectedEarnings.map((t) => t.id),
                            from: toDateOnlyString(from),
                            to: toDateOnlyString(to),
                        }),
                    });
                    showToast({
                        severity: "success",
                        summary: "Payment cleared",
                        detail: result?.receipt?.receipt_number || "Payout settlement recorded",
                    });
                    setReceiptPayload(result?.receipt || null);
                    setReceiptOpen(!!result?.receipt);
                    setSelectedEarnings([]);
                    await loadDriverEarnings(id, earningsPreset, earningsCustomRange);
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Payout failed",
                        detail: String(e.message || e),
                    });
                } finally {
                    setPayoutSaving(false);
                }
            },
        });
    }

    function requestUnapprove(row) {
        const id = row?.id ?? row;
        const label = typeof row === "object" ? (row.full_name || "").trim() : (detail?.full_name || "").trim();
        confirmDialog({
            message: `Revert onboarding completion for ${label || "this driver"}? They will show as not approved in the directory.`,
            header: "Unapprove onboarding",
            icon: "pi pi-undo",
            acceptLabel: "Unapprove",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await driverAdmin(`/admin/drivers/${id}/unapprove-onboarding`, { method: "PATCH" });
                    showToast({ severity: "success", summary: "Updated", detail: "Onboarding completion cleared" });
                    await reload();
                    await reloadOpenDetailIf({
                        dialogOpen: detailOpen,
                        shownId: detail?.id,
                        mutatedId: id,
                        fetchDetail: (i) => driverAdmin(`/admin/drivers/${i}`),
                        setDetail,
                    });
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Unapprove failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    async function openEditDriver(row) {
        setEditDriverId(row.id);
        setEditOpen(true);
        setEditLoading(true);
        setPendingDocRemovals([]);
        try {
            const d = await driverAdmin(`/admin/drivers/${row.id}`);
            const v = d.vehicle && typeof d.vehicle === "object" ? d.vehicle : {};
            const b = d.bank && typeof d.bank === "object" ? d.bank : {};
            setEditForm({
                full_name: d.full_name ?? "",
                email: d.email ?? "",
                phone: d.phone != null ? String(d.phone) : "",
                active: !!d.active,
                commission_percent: commissionRateToPercent(d.commission_rate),
                vehicle_number: v.vehicle_number != null ? String(v.vehicle_number) : "",
                vehicle_model: v.vehicle_model != null ? String(v.vehicle_model) : "",
                vehicle_colour: v.vehicle_colour != null ? String(v.vehicle_colour) : "",
                vehicle_category_id:
                    v.vehicle_category_id != null && v.vehicle_category_id !== ""
                        ? Number(v.vehicle_category_id)
                        : null,
                bank_account_holder_name: b.account_holder_name != null ? String(b.account_holder_name) : "",
                bank_name: b.bank_name != null ? String(b.bank_name) : "",
                account_number: b.account_number != null ? String(b.account_number) : "",
                transit_number: b.transit_number != null ? String(b.transit_number) : "",
                institution_number: b.institution_number != null ? String(b.institution_number) : "",
            });
            setEditDocumentKeys(
                d.document_storage_keys && typeof d.document_storage_keys === "object" ? d.document_storage_keys : {},
            );
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Could not load driver",
                detail: String(e.message || e),
            });
            setEditOpen(false);
            setEditDriverId(null);
        } finally {
            setEditLoading(false);
        }
    }

    function togglePendingDocRemoval(docType) {
        setPendingDocRemovals((prev) =>
            prev.includes(docType) ? prev.filter((t) => t !== docType) : [...prev, docType],
        );
    }

    async function saveDriverEdit() {
        if (editDriverId == null) return;
        const rate = commissionPercentToRate(editForm.commission_percent);
        if (rate == null || rate < 0.01 || rate > 1) {
            showToast({
                severity: "warn",
                summary: "Invalid commission",
                detail: "Commission must be between 1% and 100%.",
            });
            return;
        }
        setEditSaving(true);
        try {
            const body = {
                full_name: editForm.full_name,
                email: editForm.email,
                phone: editForm.phone,
                active: editForm.active,
                commission_rate: rate,
                vehicle: {
                    vehicle_number: editForm.vehicle_number,
                    vehicle_model: editForm.vehicle_model,
                    vehicle_colour: editForm.vehicle_colour,
                    vehicle_category_id: editForm.vehicle_category_id,
                },
                bank: {
                    account_holder_name: editForm.bank_account_holder_name,
                    bank_name: editForm.bank_name,
                    account_number: editForm.account_number,
                    transit_number: editForm.transit_number,
                    institution_number: editForm.institution_number,
                },
            };
            if (pendingDocRemovals.length > 0) {
                body.remove_document_types = [...pendingDocRemovals];
            }
            await driverAdmin(`/admin/drivers/${editDriverId}`, {
                method: "PATCH",
                body: JSON.stringify(body),
            });
            showToast({ severity: "success", summary: "Saved", detail: editForm.full_name || "Driver updated" });
            setEditOpen(false);
            await reload();
            await reloadOpenDetailIf({
                dialogOpen: detailOpen,
                shownId: detail?.id,
                mutatedId: editDriverId,
                fetchDetail: (i) => driverAdmin(`/admin/drivers/${i}`),
                setDetail,
            });
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Save failed",
                detail: String(e.message || e),
            });
        } finally {
            setEditSaving(false);
        }
    }

    function requestDeleteDriver(row) {
        const name = (row.full_name || "").trim() || `ID ${row.id}`;
        confirmDialog({
            message: `Permanently delete driver ${name}? Requires no assigned rides on the passenger system. This cannot be undone.`,
            header: "Delete driver",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await driverAdmin(`/admin/drivers/${row.id}`, { method: "DELETE" });
                    showToast({ severity: "success", summary: "Deleted", detail: name });
                    if (detailOpen && detail?.id === row.id) {
                        setDetailOpen(false);
                        setDetail(null);
                    }
                    await reload();
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Delete failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    const actionsBody = (row) => (
        <div className="uc-admin-table-actions">
            <Button label="View" icon="pi pi-eye" size="small" outlined onClick={() => openDetail(row.id)} />
            <Button label="Edit" icon="pi pi-pencil" size="small" outlined onClick={() => openEditDriver(row)} />
            {isOnlineStatus(row.status) ? (
                <Button
                    label="Offline"
                    icon="pi pi-power-off"
                    size="small"
                    severity="warning"
                    outlined
                    loading={statusSavingId === row.id}
                    onClick={() => setDriverOnlineOffline(row, "offline")}
                />
            ) : (
                <Button
                    label="Online"
                    icon="pi pi-wifi"
                    size="small"
                    severity="success"
                    outlined
                    loading={statusSavingId === row.id}
                    onClick={() => setDriverOnlineOffline(row, "online")}
                />
            )}
            {row.completed ? (
                <Button
                    label="Unapprove"
                    icon="pi pi-undo"
                    size="small"
                    severity="secondary"
                    onClick={() => requestUnapprove(row)}
                />
            ) : (
                <Button label="Approve" icon="pi pi-check" size="small" onClick={() => requestApprove(row)} />
            )}
            <Button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                onClick={() => requestDeleteDriver(row)}
            />
        </div>
    );

    return (
        <div>
            <ConfirmDialog />
            <AdminPageHeader
                title="Drivers"
                subtitle="Driver partners: contact info, onboarding steps, and full profile when you open a row."
                meta={
                    !loading
                        ? `${rows.length} driver${rows.length === 1 ? "" : "s"} loaded (up to ${driverLimit} recent)`
                        : undefined
                }
                actions={
                    <>
                        <span className="text-600 text-sm white-space-nowrap">Rows to load</span>
                        <Dropdown
                            value={driverLimit}
                            options={DRIVER_LIMIT_OPTIONS}
                            onChange={(e) => setDriverLimit(e.value)}
                            className="w-10rem"
                        />
                        <Button
                            label="Refresh"
                            icon="pi pi-refresh"
                            onClick={reload}
                            className="p-button-outlined"
                            disabled={loading}
                        />
                    </>
                }
            />
            {loading ? (
                <div className="flex justify-content-center p-6">
                    <ProgressSpinner />
                </div>
            ) : (
                <AdminTableShell
                    header={
                        <>
                            <i className="pi pi-table text-primary" aria-hidden />
                            <span className="uc-admin-table-toolbar-title">Driver directory</span>
                        </>
                    }
                >
                    <DataTable
                        value={rows}
                        paginator
                        paginatorDropdownAppendTo={typeof document !== "undefined" ? document.body : undefined}
                        rows={15}
                        rowsPerPageOptions={[15, 25, 50]}
                        stripedRows
                        emptyMessage="No drivers found."
                        size="small"
                        showGridlines
                        responsiveLayout="stack"
                        breakpoint="768px"
                        className="uc-admin-datatable-fluid"
                    >
                        <Column
                            field="id"
                            header="ID"
                            sortable
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.id}</span>}
                        />
                        <Column field="full_name" header="Name" sortable />
                        <Column
                            field="phone"
                            header="Phone"
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.phone ?? "—"}</span>}
                        />
                        <Column field="email" header="Email" />
                        <Column
                            field="status"
                            header="Status"
                            sortable
                            body={(r) => (
                                <Tag
                                    value={driverStatusText(r.status)}
                                    severity={isOnlineStatus(r.status) ? "success" : "secondary"}
                                />
                            )}
                        />
                        <Column
                            field="commission_rate"
                            header="Commission"
                            sortable
                            body={(r) => (
                                <span className="uc-admin-cell-nowrap">
                                    {r.commission_rate != null
                                        ? `${commissionRateToPercent(r.commission_rate)}%`
                                        : "—"}
                                </span>
                            )}
                        />
                        <Column field="personal_details" header="Personal" body={(r) => boolTag(!!r.personal_details)} />
                        <Column field="documents" header="Docs" body={(r) => boolTag(!!r.documents)} />
                        <Column field="vehicle_details" header="Vehicle" body={(r) => boolTag(!!r.vehicle_details)} />
                        <Column field="bank_details" header="Bank" body={(r) => boolTag(!!r.bank_details)} />
                        <Column field="completed" header="Done" body={(r) => boolTag(!!r.completed)} />
                        <Column header="Actions" body={actionsBody} />
                    </DataTable>
                </AdminTableShell>
            )}

            <Dialog
                header={driverDialogHeader(detail)}
                visible={detailOpen}
                style={{ width: "min(64rem, 96vw)" }}
                onHide={() => setDetailOpen(false)}
                draggable={false}
                dismissableMask
                className="uc-admin-dialog"
                pt={{
                    content: { className: classNames("uc-admin-dialog-content", "border-round-bottom-xl") },
                }}
            >
                {detailLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : detail ? (
                    <div className="flex flex-column gap-4">
                        <p className="uc-admin-dialog-lead text-600 m-0">
                            Onboarding, documents, vehicle, earnings, and bank details for this driver.
                        </p>

                        <div className="uc-admin-dialog-status">
                            <div className="font-semibold text-900 mb-2">Status (app)</div>
                            <div className="flex flex-wrap align-items-center gap-2 mb-3">
                                <Tag
                                    value={driverStatusText(detail.status)}
                                    severity={isOnlineStatus(detail.status) ? "success" : "secondary"}
                                />
                                {isOnlineStatus(detail.status) ? (
                                    <Button
                                        label="Set offline"
                                        icon="pi pi-power-off"
                                        size="small"
                                        severity="warning"
                                        outlined
                                        loading={statusSavingId === detail.id}
                                        onClick={() => setDriverOnlineOffline(detail, "offline")}
                                    />
                                ) : (
                                    <Button
                                        label="Set online"
                                        icon="pi pi-wifi"
                                        size="small"
                                        severity="success"
                                        outlined
                                        loading={statusSavingId === detail.id}
                                        onClick={() => setDriverOnlineOffline(detail, "online")}
                                    />
                                )}
                            </div>
                            <div className="font-semibold text-900 mb-2">Driver commission</div>
                            <p className="m-0 mb-3 text-900">
                                {detail.commission_rate != null
                                    ? `${commissionRateToPercent(detail.commission_rate)}% of commission pool (driver share)`
                                    : "—"}
                            </p>
                            <div className="font-semibold text-900 mb-2">Onboarding</div>
                            <div className="flex flex-wrap gap-2">
                                {detail.onboarding_status ? (
                                    <>
                                        <Tag
                                            value={`Personal: ${detail.onboarding_status.personal_details ? "OK" : "—"}`}
                                            severity={detail.onboarding_status.personal_details ? "success" : "secondary"}
                                        />
                                        <Tag
                                            value={`Docs: ${detail.onboarding_status.documents ? "OK" : "—"}`}
                                            severity={detail.onboarding_status.documents ? "success" : "secondary"}
                                        />
                                        <Tag
                                            value={`Vehicle: ${detail.onboarding_status.vehicle_details ? "OK" : "—"}`}
                                            severity={detail.onboarding_status.vehicle_details ? "success" : "secondary"}
                                        />
                                        <Tag
                                            value={`Bank: ${detail.onboarding_status.bank_details ? "OK" : "—"}`}
                                            severity={detail.onboarding_status.bank_details ? "success" : "secondary"}
                                        />
                                        <Tag
                                            value={`Completed: ${detail.onboarding_status.completed ? "Yes" : "No"}`}
                                            severity={detail.onboarding_status.completed ? "success" : "warning"}
                                        />
                                    </>
                                ) : (
                                    <span className="text-500">No status row</span>
                                )}
                            </div>
                        </div>

                        <div className="border-top-1 surface-border pt-3">
                            <div className="font-semibold text-900 mb-2">Earnings & payouts</div>
                            <p className="text-600 text-sm mt-0 mb-3">
                                Filter ride earnings, select unpaid transactions, then mark payment done to generate a
                                payout receipt.
                            </p>
                            <div className="flex flex-wrap align-items-end gap-3 mb-3">
                                <div className="flex flex-column gap-2">
                                    <label className="font-medium text-sm" htmlFor="earn-preset">
                                        Date range
                                    </label>
                                    <Dropdown
                                        inputId="earn-preset"
                                        value={earningsPreset}
                                        options={EARNINGS_RANGE_PRESETS}
                                        onChange={(e) => setEarningsPreset(e.value)}
                                        className="w-12rem"
                                    />
                                </div>
                                {earningsPreset === "custom" ? (
                                    <div className="flex flex-column gap-2">
                                        <label className="font-medium text-sm" htmlFor="earn-range">
                                            From – to
                                        </label>
                                        <Calendar
                                            inputId="earn-range"
                                            value={earningsCustomRange}
                                            onChange={(e) => setEarningsCustomRange(e.value)}
                                            selectionMode="range"
                                            readOnlyInput
                                            hideOnRangeSelection
                                            dateFormat="yy-mm-dd"
                                            showIcon
                                            className="w-18rem"
                                        />
                                    </div>
                                ) : null}
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    outlined
                                    size="small"
                                    disabled={earningsLoading}
                                    onClick={() =>
                                        loadDriverEarnings(detail.id, earningsPreset, earningsCustomRange)
                                    }
                                />
                            </div>
                            <div className="flex flex-wrap gap-3 mb-3 text-sm">
                                <span>
                                    Period earnings:{" "}
                                    <strong>{formatMoney(earningsData?.period_total)}</strong>
                                </span>
                                <span>
                                    Period unpaid:{" "}
                                    <strong>{formatMoney(earningsData?.period_unpaid)}</strong>
                                </span>
                                <span>
                                    Wallet balance:{" "}
                                    <strong>{formatMoney(earningsData?.wallet_total)}</strong>
                                </span>
                                <span>
                                    All unpaid:{" "}
                                    <strong>{formatMoney(earningsData?.unpaid_total)}</strong>
                                </span>
                            </div>
                            {earningsLoading ? (
                                <div className="flex justify-content-center p-4">
                                    <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                                </div>
                            ) : (
                                <>
                                    <DataTable
                                        value={earningsData?.transactions || []}
                                        selectionMode="checkbox"
                                        selection={selectedEarnings}
                                        onSelectionChange={(e) =>
                                            setSelectedEarnings(
                                                (e.value || []).filter(
                                                    (t) => Number(t.amount) > 0 && t.settlement_id == null,
                                                ),
                                            )
                                        }
                                        isDataSelectable={(event) =>
                                            Number(event.data?.amount) > 0 && event.data?.settlement_id == null
                                        }
                                        dataKey="id"
                                        size="small"
                                        stripedRows
                                        emptyMessage="No wallet transactions in this range."
                                        paginator={(earningsData?.transactions || []).length > 8}
                                        rows={8}
                                        className="uc-admin-datatable-fluid"
                                    >
                                        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                                        <Column field="id" header="ID" style={{ width: "4rem" }} />
                                        <Column
                                            field="created_at"
                                            header="Date"
                                            body={(r) => (
                                                <span className="uc-admin-cell-nowrap">
                                                    {r.created_at
                                                        ? String(r.created_at).replace("T", " ").slice(0, 19)
                                                        : "—"}
                                                </span>
                                            )}
                                        />
                                        <Column
                                            field="amount"
                                            header="Amount"
                                            body={(r) => formatMoney(r.amount)}
                                        />
                                        <Column field="description" header="Description" />
                                        <Column
                                            header="Paid"
                                            body={(r) =>
                                                r.settlement_id != null || r.paid ? (
                                                    <Tag value="Paid" severity="success" />
                                                ) : Number(r.amount) > 0 ? (
                                                    <Tag value="Unpaid" severity="warning" />
                                                ) : (
                                                    <Tag value="—" severity="secondary" />
                                                )
                                            }
                                        />
                                    </DataTable>
                                    <div className="flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                                        <div className="text-sm">
                                            Selected: <strong>{selectedEarnings.length}</strong> · Total:{" "}
                                            <strong>{formatMoney(selectedEarningsTotal)}</strong>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                label="Select unpaid"
                                                icon="pi pi-check-square"
                                                outlined
                                                size="small"
                                                disabled={earningsLoading || payoutSaving}
                                                onClick={() => {
                                                    const unpaid = (earningsData?.transactions || []).filter(
                                                        (t) => Number(t.amount) > 0 && t.settlement_id == null,
                                                    );
                                                    setSelectedEarnings(unpaid);
                                                }}
                                            />
                                            <Button
                                                label="Mark payment done"
                                                icon="pi pi-check-circle"
                                                disabled={selectedEarnings.length === 0 || payoutSaving}
                                                loading={payoutSaving}
                                                onClick={submitPayoutSettlement}
                                            />
                                        </div>
                                    </div>
                                    {Array.isArray(earningsData?.settlements) && earningsData.settlements.length > 0 ? (
                                        <div className="mt-4">
                                            <div className="font-semibold text-900 mb-2">Recent payout receipts</div>
                                            <ul className="list-none p-0 m-0 flex flex-column gap-2">
                                                {earningsData.settlements.slice(0, 5).map((s) => (
                                                    <li
                                                        key={s.id}
                                                        className="flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-round-lg surface-100 border-1 surface-border"
                                                    >
                                                        <span className="text-sm">
                                                            <strong>{s.receipt_number}</strong> ·{" "}
                                                            {formatMoney(s.total_amount)} · {s.transaction_count}{" "}
                                                            tx
                                                            {s.from_date || s.to_date
                                                                ? ` · ${s.from_date || "…"} → ${s.to_date || "…"}`
                                                                : ""}
                                                        </span>
                                                        <Button
                                                            label="View receipt"
                                                            icon="pi pi-file"
                                                            size="small"
                                                            outlined
                                                            onClick={async () => {
                                                                try {
                                                                    const data = await driverAdmin(
                                                                        `/admin/drivers/${detail.id}/payout-settlements/${s.id}`,
                                                                    );
                                                                    setReceiptPayload(data.receipt);
                                                                    setReceiptOpen(true);
                                                                } catch (e) {
                                                                    showToast({
                                                                        severity: "error",
                                                                        summary: "Receipt",
                                                                        detail: String(e.message || e),
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>

                        <div>
                            <div className="font-semibold text-900 mb-2">Documents</div>
                            <p className="text-600 text-sm mt-0 mb-2">
                                Signed URLs from the driver API. <strong>View</strong> opens in a new tab; <strong>Delete</strong> removes the file from the database.
                            </p>
                            {documentEntriesOrdered.length === 0 ? (
                                <p className="text-500 m-0">No documents on file.</p>
                            ) : (
                                <ul className="list-none p-0 m-0 flex flex-column gap-2">
                                    {documentEntriesOrdered.map(([key, url]) => (
                                        <li
                                            key={key}
                                            className="flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-round-lg surface-100 border-1 surface-border"
                                        >
                                            <span className="text-900 font-medium">{documentDisplayLabel(key)}</span>
                                            <div className="flex flex-wrap gap-2">
                                                <Button
                                                    label="View"
                                                    icon="pi pi-external-link"
                                                    size="small"
                                                    outlined
                                                    onClick={() => window.open(String(url), "_blank", "noopener,noreferrer")}
                                                />
                                                <Button
                                                    label="Delete"
                                                    icon="pi pi-trash"
                                                    size="small"
                                                    severity="danger"
                                                    outlined
                                                    onClick={() => requestDeleteDocumentFromDetail(key)}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {detail.vehicle ? (
                            <DetailRecordCard
                                title="Vehicle"
                                iconClass="pi pi-car"
                                data={enrichVehicleForAdminDetail(detail.vehicle, passengerVehicleTypes)}
                                labelMap={VEHICLE_FIELD_LABELS}
                                preferredOrder={VEHICLE_FIELD_ORDER}
                                skipKeys={["vehicle_category_id"]}
                            />
                        ) : null}
                        {detail.bank ? (
                            <DetailRecordCard
                                title="Bank"
                                iconClass="pi pi-building"
                                data={detail.bank}
                                labelMap={BANK_FIELD_LABELS}
                                preferredOrder={BANK_FIELD_ORDER}
                            />
                        ) : null}
                        <div className="flex flex-wrap justify-content-end gap-2 pt-3 mt-1 border-top-1 surface-border">
                            {detail.onboarding_status?.completed ? (
                                <Button
                                    label="Unapprove onboarding"
                                    icon="pi pi-undo"
                                    severity="secondary"
                                    onClick={() => requestUnapprove(detail)}
                                />
                            ) : (
                                <Button label="Approve onboarding" icon="pi pi-check" onClick={() => requestApprove(detail)} />
                            )}
                        </div>
                    </div>
                ) : null}
            </Dialog>

            <Dialog
                header={editDriverId != null ? `Edit driver #${editDriverId}` : "Edit driver"}
                visible={editOpen}
                onHide={() => setEditOpen(false)}
                style={{ width: "min(44rem, 96vw)" }}
                className="uc-admin-dialog"
                draggable={false}
                dismissableMask
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            outlined
                            onClick={() => setEditOpen(false)}
                            disabled={editSaving || editLoading}
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            loading={editSaving}
                            disabled={editLoading}
                            onClick={saveDriverEdit}
                        />
                    </div>
                }
                pt={{
                    content: { className: classNames("uc-admin-dialog-content", "border-round-bottom-xl") },
                }}
            >
                {editLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="flex flex-column gap-4 p-fluid">
                        <div>
                            <div className="text-900 font-semibold text-sm mb-3">Personal</div>
                            <div className="flex flex-column gap-3">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-name" className="font-medium text-sm">
                                        Full name
                                    </label>
                                    <InputText
                                        id="d-name"
                                        value={editForm.full_name}
                                        onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-email" className="font-medium text-sm">
                                        Email
                                    </label>
                                    <InputText
                                        id="d-email"
                                        type="email"
                                        value={editForm.email}
                                        onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-phone" className="font-medium text-sm">
                                        Phone
                                    </label>
                                    <InputText
                                        id="d-phone"
                                        value={editForm.phone}
                                        onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                    />
                                </div>
                                <div className="flex align-items-center gap-2">
                                    <InputSwitch
                                        inputId="d-active"
                                        checked={editForm.active}
                                        onChange={(e) => setEditForm((f) => ({ ...f, active: !!e.value }))}
                                    />
                                    <label htmlFor="d-active" className="font-medium text-sm">
                                        Account active
                                    </label>
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-commission" className="font-medium text-sm">
                                        Driver commission %
                                    </label>
                                    <InputNumber
                                        inputId="d-commission"
                                        value={editForm.commission_percent}
                                        onValueChange={(e) =>
                                            setEditForm((f) => ({
                                                ...f,
                                                commission_percent: e.value ?? DEFAULT_COMMISSION_PERCENT,
                                            }))
                                        }
                                        min={1}
                                        max={100}
                                        suffix="%"
                                        className="w-full"
                                    />
                                    <small className="text-600">
                                        Share of the commission pool the driver keeps (customer still sees full fare).
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="border-top-1 surface-border pt-3">
                            <div className="text-900 font-semibold text-sm mb-3">Vehicle</div>
                            <div className="flex flex-column gap-3">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-vnum" className="font-medium text-sm">
                                        Registration number
                                    </label>
                                    <InputText
                                        id="d-vnum"
                                        value={editForm.vehicle_number}
                                        onChange={(e) => setEditForm((f) => ({ ...f, vehicle_number: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-vmodel" className="font-medium text-sm">
                                        Model
                                    </label>
                                    <InputText
                                        id="d-vmodel"
                                        value={editForm.vehicle_model}
                                        onChange={(e) => setEditForm((f) => ({ ...f, vehicle_model: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-vcol" className="font-medium text-sm">
                                        Colour
                                    </label>
                                    <InputText
                                        id="d-vcol"
                                        value={editForm.vehicle_colour}
                                        onChange={(e) => setEditForm((f) => ({ ...f, vehicle_colour: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-vcat" className="font-medium text-sm">
                                        Vehicle category
                                    </label>
                                    <Dropdown
                                        inputId="d-vcat"
                                        value={editForm.vehicle_category_id}
                                        options={vehicleCategoryDropdownOptions}
                                        onChange={(e) => setEditForm((f) => ({ ...f, vehicle_category_id: e.value }))}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select category"
                                        className="w-full"
                                        filter={passengerVehicleTypes.length > 6}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-top-1 surface-border pt-3">
                            <div className="text-900 font-semibold text-sm mb-3">Bank</div>
                            <div className="flex flex-column gap-3">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-bh" className="font-medium text-sm">
                                        Account holder name
                                    </label>
                                    <InputText
                                        id="d-bh"
                                        value={editForm.bank_account_holder_name}
                                        onChange={(e) =>
                                            setEditForm((f) => ({ ...f, bank_account_holder_name: e.target.value }))
                                        }
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-bn" className="font-medium text-sm">
                                        Bank name
                                    </label>
                                    <InputText
                                        id="d-bn"
                                        value={editForm.bank_name}
                                        onChange={(e) => setEditForm((f) => ({ ...f, bank_name: e.target.value }))}
                                    />
                                </div>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="d-acct" className="font-medium text-sm">
                                        Account number
                                    </label>
                                    <InputText
                                        id="d-acct"
                                        value={editForm.account_number}
                                        onChange={(e) => setEditForm((f) => ({ ...f, account_number: e.target.value }))}
                                    />
                                </div>
                                <div className="grid">
                                    <div className="col-12 sm:col-6">
                                        <div className="flex flex-column gap-2">
                                            <label htmlFor="d-tr" className="font-medium text-sm">
                                                Transit / routing
                                            </label>
                                            <InputText
                                                id="d-tr"
                                                value={editForm.transit_number}
                                                onChange={(e) =>
                                                    setEditForm((f) => ({ ...f, transit_number: e.target.value }))
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 sm:col-6">
                                        <div className="flex flex-column gap-2">
                                            <label htmlFor="d-in" className="font-medium text-sm">
                                                Institution number
                                            </label>
                                            <InputText
                                                id="d-in"
                                                value={editForm.institution_number}
                                                onChange={(e) =>
                                                    setEditForm((f) => ({ ...f, institution_number: e.target.value }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-top-1 surface-border pt-3">
                            <div className="text-900 font-semibold text-sm mb-2">Documents</div>
                            <p className="text-600 text-xs line-height-3 mt-0 mb-3">
                                Admins cannot attach files here. Queue <strong>Delete document</strong> to remove the row from the
                                database; the driver must upload again from the app. Saving applies deletions and refreshes
                                onboarding checkmarks.
                            </p>
                            <div className="flex flex-column gap-3">
                                {DOCUMENT_TYPE_OPTIONS.map(({ type, label }) => {
                                    const hadFile = !!editDocumentKeys[type] && !pendingDocRemovals.includes(type);
                                    const queuedRemove = pendingDocRemovals.includes(type);
                                    return (
                                        <div
                                            key={type}
                                            className="surface-50 border-round-lg p-3 border-1 surface-border flex flex-column gap-2"
                                        >
                                            <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                                                <span className="font-medium text-sm text-900">{label}</span>
                                                {hadFile ? (
                                                    <Tag value="On file" severity="success" className="text-xs" />
                                                ) : queuedRemove ? (
                                                    <Tag value="Deleting on save" severity="warning" className="text-xs" />
                                                ) : (
                                                    <Tag value="None" severity="secondary" className="text-xs" />
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {hadFile ? (
                                                    <Button
                                                        type="button"
                                                        label="Delete document"
                                                        icon="pi pi-trash"
                                                        size="small"
                                                        severity="danger"
                                                        outlined
                                                        onClick={() => togglePendingDocRemoval(type)}
                                                    />
                                                ) : queuedRemove ? (
                                                    <Button
                                                        type="button"
                                                        label="Undo delete"
                                                        icon="pi pi-undo"
                                                        size="small"
                                                        className="p-button-outlined"
                                                        onClick={() => togglePendingDocRemoval(type)}
                                                    />
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog
                header={
                    approveDriver
                        ? `Approve ${(approveDriver.full_name || "").trim() || `driver #${approveDriver.id}`}`
                        : "Approve onboarding"
                }
                visible={approveOpen}
                onHide={() => !approveSaving && setApproveOpen(false)}
                style={{ width: "min(28rem, 96vw)" }}
                className="uc-admin-dialog"
                draggable={false}
                dismissableMask={!approveSaving}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            outlined
                            disabled={approveSaving}
                            onClick={() => setApproveOpen(false)}
                        />
                        <Button
                            label="Approve"
                            icon="pi pi-check"
                            loading={approveSaving}
                            onClick={submitApproveOnboarding}
                        />
                    </div>
                }
            >
                <div className="flex flex-column gap-3 p-fluid">
                    <p className="text-600 text-sm m-0 line-height-3">
                        Set the vehicle category and how much of each ride&apos;s commission pool this driver keeps.
                        Customers always see the full fare; drivers see estimated earnings after this share is applied.
                    </p>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="ap-vcat" className="font-medium text-sm">
                            Vehicle category
                        </label>
                        <Dropdown
                            inputId="ap-vcat"
                            value={approveForm.vehicle_category_id}
                            options={vehicleCategoryDropdownOptions.filter((o) => o.value != null)}
                            onChange={(e) => {
                                const nextId = e.value;
                                const selectedType = (passengerVehicleTypes || []).find(
                                    (t) => Number(t.id) === Number(nextId),
                                );
                                setApproveForm((f) => ({
                                    ...f,
                                    vehicle_category_id: nextId,
                                    capacity:
                                        selectedType?.capacity != null
                                            ? Number(selectedType.capacity)
                                            : f.capacity,
                                }));
                            }}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select category"
                            className="w-full"
                            filter={passengerVehicleTypes.length > 6}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="ap-capacity" className="font-medium text-sm">
                            Vehicle capacity
                        </label>
                        <InputNumber
                            inputId="ap-capacity"
                            value={approveForm.capacity}
                            onValueChange={(e) =>
                                setApproveForm((f) => ({
                                    ...f,
                                    capacity: e.value,
                                }))
                            }
                            min={1}
                            max={20}
                            showButtons
                            className="w-full"
                            placeholder="Passenger seats"
                        />
                        <small className="text-600">
                            Seat capacity for this vehicle category (saved on the vehicle type catalog).
                        </small>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="ap-commission" className="font-medium text-sm">
                            Driver commission %
                        </label>
                        <InputNumber
                            inputId="ap-commission"
                            value={approveForm.commission_percent}
                            onValueChange={(e) =>
                                setApproveForm((f) => ({
                                    ...f,
                                    commission_percent: e.value ?? DEFAULT_COMMISSION_PERCENT,
                                }))
                            }
                            min={1}
                            max={100}
                            suffix="%"
                            className="w-full"
                        />
                        <small className="text-600">
                            Share of the commission pool the driver keeps (e.g. 75% → driver earns 75% of the pool;
                            company keeps the rest). Applied on every completed ride and wallet credit.
                        </small>
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Driver payout receipt"
                visible={receiptOpen}
                onHide={() => setReceiptOpen(false)}
                style={{ width: "min(36rem, 96vw)" }}
                className="uc-admin-dialog"
                draggable={false}
                dismissableMask
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Print"
                            icon="pi pi-print"
                            outlined
                            disabled={!receiptPayload}
                            onClick={() => window.print()}
                        />
                        <Button label="Close" icon="pi pi-times" outlined onClick={() => setReceiptOpen(false)} />
                    </div>
                }
            >
                {receiptPayload ? (
                    <div className="flex flex-column gap-3" id="uc-admin-payout-receipt">
                        <div className="surface-100 border-round-lg p-3 border-1 surface-border">
                            <div className="text-900 font-semibold text-lg mb-2">
                                {receiptPayload.receipt_number}
                            </div>
                            <div className="text-sm flex flex-column gap-1">
                                <span>
                                    Driver: <strong>{receiptPayload.driver_name || `#${receiptPayload.driver_id}`}</strong>
                                    {receiptPayload.driver_phone ? ` · ${receiptPayload.driver_phone}` : ""}
                                </span>
                                <span>
                                    Period:{" "}
                                    <strong>
                                        {receiptPayload.from || "…"} → {receiptPayload.to || "…"}
                                    </strong>
                                </span>
                                <span>
                                    Transactions: <strong>{receiptPayload.transaction_count}</strong>
                                </span>
                                <span>
                                    Total paid: <strong>{formatMoney(receiptPayload.total_amount)}</strong>
                                </span>
                                <span className="text-green-700 font-medium">Payment completed</span>
                            </div>
                        </div>
                        <DataTable
                            value={receiptPayload.transactions || []}
                            size="small"
                            emptyMessage="No lines"
                            className="uc-admin-datatable-fluid"
                        >
                            <Column field="id" header="ID" style={{ width: "4rem" }} />
                            <Column
                                field="created_at"
                                header="Earned"
                                body={(r) =>
                                    r.created_at ? String(r.created_at).replace("T", " ").slice(0, 19) : "—"
                                }
                            />
                            <Column field="amount" header="Amount" body={(r) => formatMoney(r.amount)} />
                            <Column field="description" header="Description" />
                        </DataTable>
                    </div>
                ) : null}
            </Dialog>
        </div>
    );
}
